import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getCookie } from './auth/cookies';
import { toast } from 'react-toastify';
import { 
  FaUserFriends, 
  FaSave, 
  FaEdit, 
  FaInfoCircle, 
  FaClipboardList,
  FaUserEdit,
  FaRegTrashAlt,
  FaSpinner
} from "react-icons/fa";
import SubmissionsPage from './submissionspage';

function TeamPage() {
    // States to hold form inputs and UI states
    const [teamName, setTeamName] = useState('');
    const [members, setMembers] = useState('');
    const [description, setDescription] = useState('');
    const [submissions, setSubmissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formChanged, setFormChanged] = useState(false);
    const [error, setError] = useState(null);
    const [teamContent, setTeamContent] = useState(null);

    // Update formChanged state when inputs change
    useEffect(() => {
        setFormChanged(true);
    }, [teamName, members, description]);

    // Handle saving team information
    const handleSave = async () => {
        if (!teamName.trim()) {
            toast.warning('Please enter a team name');
            return;
        }

        try {
            setIsSaving(true);
            await axios.post('/api/fill_team_info/', {
                name: teamName,
                members: members,
                description: description
            },
            { headers: { 'X-CSRFToken': getCookie('csrftoken') } });
            
            toast.success('Team information saved successfully!');
            setFormChanged(false);
            fetchSubmissions();
        } catch (err) {
            console.error('Error saving team info:', err);
            toast.error(err.response?.data?.error || 'An error occurred while saving. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Fetch team information
    const fetchTeamInfo = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await axios.get('/api/get_team_info/');
            
            setTeamName(response.data.name || '');
            setMembers(response.data.members || '');
            setDescription(response.data.description || '');
            
            setFormChanged(false);
        } catch (err) {
            console.error('Error fetching team info:', err);
            setError(err.response?.data?.error || 'Failed to load team information');
            toast.error(`Error loading team info: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch team content
    const fetchTeamContent = async () => {
        try {
            const response = await axios.get('/api/load_team_content/', {
                headers: { 'X-CSRFToken': getCookie('csrftoken') }
            });
            setTeamContent(response.data.content);
        } catch (err) {
            console.error('Error loading team content:', err);
            toast.error(`Error loading team content: ${err.message}`);
        }
    };

    // Fetch team information and submissions on component mount
    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([
                fetchTeamInfo(),
                fetchSubmissions(),
                fetchTeamContent()
            ]);
        };
        
        fetchData();
    }, []);

    // Fetch submissions
    const fetchSubmissions = async () => {
        try {
            const response = await axios.get('/api/get_submissions/', { 
                headers: { 'X-CSRFToken': getCookie('csrftoken') } 
            });
            setSubmissions(response.data.submissions || []);
        } catch (err) {
            console.error('Error fetching submissions:', err);
            toast.error(`Error fetching submissions: ${err.message}`);
        }
    };

    // Handle submission deletion
    const handleDelete = async (submission_id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this submission?");
        if (!confirmDelete) return;
    
        try {
            await axios.post(`/api/delete_submission/`, 
                { submission_id: submission_id },
                { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
            );
            
            toast.success('Submission deleted successfully!');
            // Remove deleted submission from list
            setSubmissions(submissions.filter(submission => submission.id !== submission_id));
        } catch (err) {
            console.error('Error deleting submission:', err);
            toast.error(`Error deleting submission: ${err.message}`);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-200 pt-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-secondary-dark dark:text-white mb-2 flex items-center">
                        <FaUserFriends className="mr-3" />
                        {teamContent?.header?.title || ''}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {teamContent?.header?.subtitle || ''}
                    </p>
                </header>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <FaSpinner className="animate-spin text-secondary text-3xl mr-3" />
                        <span>Loading team information...</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 rounded-lg mb-8">
                        <div className="flex items-start">
                            <FaInfoCircle className="text-red-500 mt-1 flex-shrink-0" />
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                                    Error Loading Team Information
                                </h3>
                                <p className="mt-2 text-red-700 dark:text-red-400">{error}</p>
                                <button 
                                    onClick={fetchTeamInfo}
                                    className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Team Information Form */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                            <div className="bg-secondary-dark p-4">
                                <h2 className="text-xl font-semibold text-white flex items-center">
                                    <FaUserEdit className="mr-2" />
                                    Team Information
                                </h2>
                            </div>
                            
                            <div className="p-6 space-y-6">
                                {/* Team Name Input */}
                                <div>
                                    <label htmlFor="teamName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {teamContent?.form?.team_name?.label || ''} <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        id="teamName"
                                        type="text"
                                        className="w-full p-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
                                        placeholder={teamContent?.form?.team_name?.placeholder || ""}
                                        value={teamName}
                                        onChange={(e) => setTeamName(e.target.value)}
                                    />
                                </div>

                                {/* Team Members Input */}
                                <div>
                                    <label htmlFor="members" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {teamContent?.form?.team_members?.label || ''}
                                    </label>
                                    <textarea 
                                        id="members"
                                        className="w-full h-24 p-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
                                        placeholder={teamContent?.form?.team_members?.placeholder || ""}
                                        value={members}
                                        onChange={(e) => setMembers(e.target.value)}
                                    ></textarea>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        {teamContent?.form?.team_members?.help_text || ''}
                                    </p>
                                </div>

                                {/* Team Description Input */}
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {teamContent?.form?.team_description?.label || ''}
                                    </label>
                                    <textarea 
                                        id="description"
                                        className="w-full h-32 p-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
                                        placeholder={teamContent?.form?.team_description?.placeholder || ""}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    ></textarea>
                                </div>

                                {/* Save Button */}
                                <div className="pt-2">
                                    <button
                                        className={`w-full px-6 py-3 rounded-md flex items-center justify-center transition-colors ${
                                            formChanged 
                                                ? 'bg-secondary hover:bg-secondary-dark text-white' 
                                                : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-400 cursor-not-allowed'
                                        }`}
                                        onClick={handleSave}
                                        disabled={!formChanged || isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <FaSpinner className="animate-spin mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {teamContent?.form?.save_button || ''}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Team Information Card */}
                        <div className="space-y-8">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-blue-600 p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaInfoCircle className="mr-2" />
                                        {teamContent?.profile?.section_title || ''}
                                    </h2>
                                </div>
                                
                                <div className="p-6">
                                    <div className="mb-6 text-center pb-4 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-2xl font-bold text-secondary-dark dark:text-secondary-light mb-1">
                                            {teamName || teamContent?.profile?.default_team_name || ''}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {teamContent?.profile?.help_text || ''}
                                        </p>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                                                Team Members
                                            </h4>
                                            <div className="bg-gray-50 dark:bg-gray-750 p-3 rounded-md">
                                                {members ? (
                                                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
                                                        {members.split(/[\n\r,]+/).filter(member => member.trim()).map((member, index) => (
                                                            <li key={index}>{member.trim()}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-gray-500 dark:text-gray-400 italic">
                                                        {teamContent?.profile?.fallback_members || ''}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                                                Team Description
                                            </h4>
                                            <div className="bg-gray-50 dark:bg-gray-750 p-3 rounded-md max-h-40 overflow-y-auto">
                                                {description ? (
                                                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                                                        {description}
                                                    </p>
                                                ) : (
                                                    <p className="text-gray-500 dark:text-gray-400 italic">
                                                        {teamContent?.profile?.fallback_description || ''}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Quick Help Card */}
                            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-5 border-l-4 border-indigo-500">
                                <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-300 mb-2">
                                    {teamContent?.tips?.section_title || ''}
                                </h3>
                                <ul className="list-disc pl-5 text-indigo-700 dark:text-indigo-300 space-y-1 text-sm">
                                    {teamContent?.tips?.items ? (
                                        teamContent.tips.items.map((tip, index) => (
                                            <li key={index}>{tip}</li>
                                        ))
                                    ) : (
                                        <>
                                            <li>Adding team members helps everyone know who contributed</li>
                                            <li>A good team description explains your approach and methodology</li>
                                            <li>Don't forget to save your changes before leaving this page</li>
                                            <li>You can update your team information at any time</li>
                                        </>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submissions Section */}
                {!isLoading && !error && (
                    <div className="mt-12">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                            <div className="bg-green-600 p-4">
                                <h2 className="text-xl font-semibold text-white flex items-center">
                                    <FaClipboardList className="mr-2" />
                                    {teamContent?.submissions?.section_title || ''}
                                </h2>
                            </div>
                            
                            <div className="p-6">
                                {submissions.length > 0 ? (
                                    <SubmissionsPage 
                                        submissions={submissions} 
                                        showDelete={true} 
                                        setSubmissions={setSubmissions} 
                                        onDelete={handleDelete}
                                    />
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 dark:text-gray-400">
                                            {teamContent?.submissions?.empty_state || ''}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TeamPage;